// Reporting's LINE ITEMS section (owner, 2026-11-04: "Please also deploy a
// helper to add Line Items by Spend, Line Items by Vendor, Line Items by
// Account, Line Items by Department, and Line Items by Vehicle.")
//
// Read as the catalogue reads "Accounts by Spend" (the accounts ranked by
// spend): Line Items by Spend is the line items ranked by their totals; the
// other four are how many line items each vendor, account, department or
// vehicle has, largest first.
//
// FIVE CHARTS, every one answering Show Group Rows as the line table does: on,
// the lines of a group are one (one bar at the group's total, or one in a
// count); off, every real line by itself.
//  * by Spend, by Vendor and by Vehicle read `ChartData.lineItems`
//    (`GET /analytics/line-items`, `docs/api.md` §16.14).
//  * by Account and by Department are the charts that were Line Items Per
//    Account and Per Department (D-465): the same figure from §16.8, so they
//    MOVED here under their ids rather than being drawn twice (`codeCharts.ts`).
//
// TAX AND SHIPPING (D-420): their lines are real lines with spend, so they are
// bars in by Spend (with groups shown, the Tax group is one bar, "Tax") and
// count in by Vendor; they hold no Account or Department, so they are in
// neither of those; in by Vehicle, like any line, only when a vehicle tag is on
// them.
//
// No I/O here: the screen does the reads and hands the results in.
import type { AnalyticsLineItemSpendRow, AnalyticsLineItemVehicleRow, AnalyticsLineItemVendorRow } from '../api/types'
import type { BarRowData } from '../components/DashboardCharts'
import { NO_KEY, compactMoney, wholeNumber, type ChartData, type ReportChart } from './reportCharts'
import { SECTION_COLORS } from './theme'

/** A chart whose rows, and whether they were cut, follow Show Group Rows. */
export interface GroupRowsChart extends ReportChart {
  /** Set on a chart detailing line items: the screen offers Show Group Rows beside it. */
  lineItems?: true
  /** The value heading with Show Group Rows on, where it is not the Line Items count's. */
  groupedValueHeader?: string
  /** Rows, largest first; `showGroupRows` defaults on, as the line table opens. */
  rows: (data: ChartData, showGroupRows?: boolean) => BarRowData[] | null
  truncated: (data: ChartData, showGroupRows?: boolean) => boolean
}

/** The section's heading in the Reports rail. */
const LINE_ITEMS_GROUP = 'Line Items'

const color = SECTION_COLORS.spend
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
const groupsNote = (showGroupRows: boolean) => (showGroupRows ? ', each group counted once' : '')

/** A bar of one colour. */
function bar(key: string, label: string, total: number, display: string, title: string): BarRowData {
  return { key, label, total, display, segments: [{ color, value: total }], title }
}

/** Largest first, ties by label so they read alphabetically. */
const largestFirst = (a: BarRowData, b: BarRowData) => b.total - a.total || a.label.localeCompare(b.label)

/** A line (or group) as a person finds it: "<vendor> · <invoice number> · <description>". */
function lineItemLabel(row: Pick<AnalyticsLineItemSpendRow, 'vendor_name' | 'external_invoice_id' | 'description'>): string {
  return `${row.vendor_name ?? 'No Vendor'} · ${row.external_invoice_id ?? 'No Invoice Number'} · ${row.description ?? 'No Description'}`
}

/**
 * A vehicle as its saved copy names it, word for word as Spend Per Vehicle
 * names it (`vehicleLabel` in `entityCharts.ts`, which does not export it):
 * the fleet number, then make and model where the copy has them.
 */
function vehicleLabel(row: Pick<AnalyticsLineItemVehicleRow, 'number' | 'make' | 'model'>): string {
  const kind = [row.make, row.model].filter((part) => part !== null && part.trim() !== '').join(' ')
  const number = row.number ?? 'Vehicle With No Saved Copy'
  return kind === '' ? number : `${number} · ${kind}`
}

function spendBars(rows: readonly AnalyticsLineItemSpendRow[], showGroupRows: boolean): BarRowData[] {
  return rows
    .filter((row) => row.total > 0)
    .map((row) => {
      const label = lineItemLabel(row)
      const lines = showGroupRows ? ` across ${plural(row.line_count, 'line', 'lines')}` : ''
      return bar(row.id, label, row.total, compactMoney(row.total), `${label}: ${compactMoney(row.total)}${lines}`)
    })
    // Stable: equal totals keep the server's order (vendor, invoice number, line).
    .sort((a, b) => b.total - a.total)
}

const count = (row: { line_item_count: number; grouped_line_item_count: number }, showGroupRows: boolean) =>
  showGroupRows ? row.grouped_line_item_count : row.line_item_count

function countBars<T extends { line_item_count: number; grouped_line_item_count: number }>(
  rows: readonly T[],
  showGroupRows: boolean,
  key: (row: T) => string,
  label: (row: T) => string,
): BarRowData[] {
  return rows
    .filter((row) => count(row, showGroupRows) > 0)
    .map((row) => {
      const n = count(row, showGroupRows)
      return bar(key(row), label(row), n, wholeNumber(n), `${label(row)}: ${plural(n, 'line item', 'line items')}${groupsNote(showGroupRows)}`)
    })
    .sort(largestFirst)
}

const vendorLabel = (row: AnalyticsLineItemVendorRow) => row.vendor_name ?? row.vendor_id ?? 'No Vendor'

const BY_SPEND: GroupRowsChart = {
  id: 'line-items-spend',
  group: LINE_ITEMS_GROUP,
  title: 'Line Items by Spend',
  labelHeader: 'Line Item',
  valueHeader: 'Spend',
  groupedValueHeader: 'Spend',
  lineItems: true,
  // NOT CLICKABLE: no criterion names one line.
  rows: (data, showGroupRows = true) => {
    const read = data.lineItems ?? null
    return read === null ? null : spendBars(showGroupRows ? read.grouped_lines : read.lines, showGroupRows)
  },
  truncated: (data, showGroupRows = true) =>
    (showGroupRows ? data.lineItems?.grouped_lines_truncated : data.lineItems?.lines_truncated) === true,
}

const BY_VENDOR: GroupRowsChart = {
  id: 'line-items-vendor',
  group: LINE_ITEMS_GROUP,
  title: 'Line Items by Vendor',
  labelHeader: 'Vendor',
  valueHeader: 'Line Items',
  lineItems: true,
  filter: 'vendors',
  rows: (data, showGroupRows = true) => {
    const read = data.lineItems ?? null
    // No Vendor is keyed `NO_KEY`, which no bar click acts on.
    return read === null ? null : countBars(read.vendors, showGroupRows, (r) => r.vendor_id ?? NO_KEY, vendorLabel)
  },
  truncated: (data) => data.lineItems?.vendors_truncated === true,
}

const BY_VEHICLE: GroupRowsChart = {
  id: 'line-items-vehicle',
  group: LINE_ITEMS_GROUP,
  title: 'Line Items by Vehicle',
  labelHeader: 'Vehicle',
  valueHeader: 'Line Items',
  lineItems: true,
  // NOT CLICKABLE: no criterion names a vehicle.
  rows: (data, showGroupRows = true) => {
    const read = data.lineItems ?? null
    return read === null ? null : countBars(read.vehicles, showGroupRows, (r) => r.vehicle_id, vehicleLabel)
  },
  truncated: (data) => data.lineItems?.vehicles_truncated === true,
}

/**
 * The section in the owner's order: by Spend, by Vendor, by Account, by
 * Department, by Vehicle. `byAccount` and `byDepartment` are the charts
 * `codeCharts.ts` builds over §16.8.
 */
export function lineItemsSection(byAccount: GroupRowsChart, byDepartment: GroupRowsChart): GroupRowsChart[] {
  return [BY_SPEND, BY_VENDOR, byAccount, byDepartment, BY_VEHICLE]
}
